import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import ClockCard from './ClockCard';
import AddClockSheet from './AddClockSheet';
import FeaturedHQCard from './FeaturedHQCard';
import MeetingFinder from './MeetingFinder';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Clock, CalendarOff, Share2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { fetchAllHolidays } from '@/utils/holidays';
import { useAuth, apiClient, formatApiErrorDetail } from '@/context/AuthContext';

const AUSTIN_CLOCK = {
  id: 'austin-pinned',
  timezone: 'America/Chicago',
  city: 'Austin',
  customName: 'Austin, TX',
  pinned: true,
  workStartHour: 9,
  workEndHour: 19,
};

const DEFAULT_OTHER_CLOCKS = [
  { id: '1', timezone: 'Europe/London', city: 'London', customName: 'London, UK', memberName: '', workStartHour: 9, workEndHour: 19 },
  { id: '2', timezone: 'Australia/Melbourne', city: 'Melbourne', customName: 'Melbourne, Australia', memberName: '', workStartHour: 9, workEndHour: 19 },
  { id: '3', timezone: 'America/Los_Angeles', city: 'Los Angeles', customName: 'Los Angeles, CA', memberName: '', workStartHour: 9, workEndHour: 19 },
  { id: '5', timezone: 'America/Chicago', city: 'Montgomery', customName: 'Montgomery, AL', memberName: '', workStartHour: 9, workEndHour: 19 },
  { id: '6', timezone: 'Europe/Copenhagen', city: 'Copenhagen', customName: 'Copenhagen, Denmark', memberName: 'Dennis', workStartHour: 9, workEndHour: 19 },
];

const DENNIS_CLOCK = {
  id: 'dennis-cph',
  timezone: 'Europe/Copenhagen',
  city: 'Copenhagen',
  customName: 'Copenhagen, Denmark',
  memberName: 'Dennis',
  workStartHour: 9,
  workEndHour: 19,
};

const MAX_CLOCKS = 10;

const TimezoneDashboard = () => {
  const { isAdmin } = useAuth();
  const [clocks, setClocks] = useState([]);
  const [format24hr, setFormat24hr] = useState(false);
  const [weekendMode, setWeekendMode] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingClock, setEditingClock] = useState(null);
  const [holidays, setHolidays] = useState({});
  const [pendingSharedConfig, setPendingSharedConfig] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Guard: non-admins get a toast and the mutation is blocked
  const requireAdmin = () => {
    if (!isAdmin) {
      toast.error('Admin access required', {
        description: 'Ask your admin to make changes to the dashboard.',
        icon: '🔒',
      });
      return false;
    }
    return true;
  };

  // Persist to backend (admin-only) — debounced via setTimeout in effect below
  const saveConfig = async (next) => {
    try {
      await apiClient.put('/dashboard/config', next);
    } catch (err) {
      // Members will hit 403 — silently ignore since their UI prevents mutations anyway
      if (err.response?.status !== 403) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to save');
      }
    }
  };

  useEffect(() => {
    // Check URL for shared config first - show merge/replace dialog instead of auto-applying
    const urlParams = new URLSearchParams(window.location.search);
    const sharedConfig = urlParams.get('config');
    let parsedShared = null;
    if (sharedConfig) {
      try {
        parsedShared = JSON.parse(atob(decodeURIComponent(sharedConfig)));
      } catch (err) {
        toast.error('Invalid shared dashboard link');
      }
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Load from backend (shared team dashboard)
    apiClient
      .get('/dashboard/config')
      .then((res) => {
        const data = res.data || {};
        const savedClocks = Array.isArray(data.clocks) ? data.clocks : [];
        // First load for the team? Seed with team defaults (admin auto-saves)
        if (savedClocks.length === 0) {
          setClocks(DEFAULT_OTHER_CLOCKS);
        } else {
          setClocks(savedClocks);
        }
        setFormat24hr(!!data.format24hr);
        setWeekendMode(!!data.weekendMode);
      })
      .catch(() => {
        setClocks(DEFAULT_OTHER_CLOCKS);
      })
      .finally(() => {
        setIsLoaded(true);
        if (parsedShared && Array.isArray(parsedShared.clocks)) {
          setPendingSharedConfig(parsedShared);
        }
      });
  }, []);

  // Fetch public holidays whenever the timezone list changes
  useEffect(() => {
    if (!isLoaded) return;
    const allTimezones = [AUSTIN_CLOCK.timezone, ...clocks.map((c) => c.timezone)];
    fetchAllHolidays(allTimezones).then(setHolidays);
  }, [clocks, isLoaded]);

  // Push config to backend whenever state changes (admin only). Members can still toggle
  // their personal 24h/weekend display locally without affecting others.
  useEffect(() => {
    if (!isLoaded || !isAdmin) return;
    const t = setTimeout(() => {
      saveConfig({ clocks, format24hr, weekendMode });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clocks, format24hr, weekendMode, isLoaded, isAdmin]);

  const handleDragEnd = (event) => {
    if (!requireAdmin()) return;
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setClocks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      toast.success('Clock position updated');
    }
  };

  const totalClocks = clocks.length + 1;

  const handleAddClock = (clockData) => {
    if (!requireAdmin()) return;
    if (totalClocks >= MAX_CLOCKS) {
      toast.error(`Maximum ${MAX_CLOCKS} clocks allowed`);
      return;
    }
    const newClock = { id: Date.now().toString(), ...clockData };
    setClocks([...clocks, newClock]);
    toast.success('Clock added successfully');
  };

  const handleEditClock = (clockData) => {
    if (!requireAdmin()) return;
    setClocks(
      clocks.map((clock) =>
        clock.id === editingClock.id ? { ...clock, ...clockData } : clock
      )
    );
    setEditingClock(null);
    toast.success('Clock updated successfully');
  };

  const handleDeleteClock = (id) => {
    if (!requireAdmin()) return;
    setClocks(clocks.filter((clock) => clock.id !== id));
    toast.success('Clock removed');
  };

  const openEditSheet = (clock) => {
    if (!requireAdmin()) return;
    setEditingClock(clock);
    setIsSheetOpen(true);
  };

  const openAddSheet = () => {
    if (!requireAdmin()) return;
    setIsSheetOpen(true);
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
    setEditingClock(null);
  };

  const handleShare = async () => {
    // Strip avatars to keep URL short and shareable
    const clocksToShare = clocks.map(({ avatar, ...rest }) => rest);
    const config = { clocks: clocksToShare, format24hr, weekendMode };
    const encoded = encodeURIComponent(btoa(JSON.stringify(config)));
    const shareUrl = `${window.location.origin}${window.location.pathname}?config=${encoded}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied! Send it to your team', {
        description: 'Note: avatars are excluded from share links',
      });
    } catch (err) {
      // Fallback - show URL in a prompt for manual copy
      window.prompt('Copy this share link:', shareUrl);
    }
  };

  const applySharedReplace = () => {
    if (!pendingSharedConfig) return;
    if (!requireAdmin()) {
      setPendingSharedConfig(null);
      return;
    }
    const sharedClocks = (pendingSharedConfig.clocks || []).filter(
      (c) => c.id !== AUSTIN_CLOCK.id && !c.pinned
    );
    setClocks(sharedClocks);
    setFormat24hr(pendingSharedConfig.format24hr || false);
    setWeekendMode(pendingSharedConfig.weekendMode || false);
    setPendingSharedConfig(null);
    toast.success('Dashboard replaced with shared configuration');
  };

  const applySharedMerge = () => {
    if (!pendingSharedConfig) return;
    if (!requireAdmin()) {
      setPendingSharedConfig(null);
      return;
    }
    const incoming = (pendingSharedConfig.clocks || []).filter(
      (c) => c.id !== AUSTIN_CLOCK.id && !c.pinned
    );
    // Avoid duplicates: skip incoming clocks whose timezone+customName match an existing one
    const existingKeys = new Set(
      clocks.map((c) => `${c.timezone}::${c.customName.toLowerCase()}`)
    );
    const newClocks = incoming
      .filter((c) => !existingKeys.has(`${c.timezone}::${c.customName.toLowerCase()}`))
      .map((c) => ({ ...c, id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` }));

    const merged = [...clocks, ...newClocks];
    if (merged.length + 1 > MAX_CLOCKS) {
      toast.error(`Cannot merge: would exceed ${MAX_CLOCKS} clocks. Some clocks were skipped.`);
      setClocks(merged.slice(0, MAX_CLOCKS - 1));
    } else {
      setClocks(merged);
      toast.success(`Merged ${newClocks.length} new clock${newClocks.length === 1 ? '' : 's'}`);
    }
    setPendingSharedConfig(null);
  };

  const cancelShared = () => {
    setPendingSharedConfig(null);
    toast.info('Shared configuration discarded');
  };

  const allClocksForFinder = [AUSTIN_CLOCK, ...clocks];

  return (
    <div>
      <div className="max-w-7xl mx-auto px-6 py-12 md:px-12 md:py-20">
        {/* Tagline */}
        <div className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-zinc-500 mb-6" data-testid="dashboard-tagline">
          // faith · innovation · excellence
        </div>

        <div className="mb-12 flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8">
          <div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[0.95]" data-testid="dashboard-title">
              World.
              <br />
              <span className="text-[#735AE2]">Time.</span>
              <br />
              Sync.
            </h1>
            <p className="font-body text-base text-zinc-400 max-w-lg mt-6 leading-relaxed">
              A real-time timezone dashboard engineered for Grace the Geek Studios.
              Track every team member across the globe — headquartered in Austin, TX.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-wrap">
            {!isAdmin && (
              <div className="flex items-center gap-2 border border-amber-500/40 bg-amber-500/5 px-4 py-2.5" data-testid="view-only-badge">
                <Lock className="w-4 h-4 text-amber-400" />
                <span className="font-mono text-xs uppercase tracking-widest text-amber-300">
                  View only
                </span>
              </div>
            )}

            <label className="flex items-center gap-3 border border-white/[0.12] px-4 py-2.5 hover:border-white/30 transition-colors cursor-pointer">
              <Clock className="w-4 h-4 text-[#735AE2]" />
              <Label htmlFor="format-toggle" className="text-white font-mono text-xs uppercase tracking-widest cursor-pointer">
                24-hour
              </Label>
              <Switch
                id="format-toggle"
                data-testid="format-toggle"
                checked={format24hr}
                onCheckedChange={setFormat24hr}
                className="data-[state=checked]:bg-[#735AE2]"
              />
            </label>

            <label className="flex items-center gap-3 border border-white/[0.12] px-4 py-2.5 hover:border-white/30 transition-colors cursor-pointer">
              <CalendarOff className="w-4 h-4 text-[#735AE2]" />
              <Label htmlFor="weekend-toggle" className="text-white font-mono text-xs uppercase tracking-widest cursor-pointer">
                Dim weekends
              </Label>
              <Switch
                id="weekend-toggle"
                data-testid="weekend-toggle"
                checked={weekendMode}
                onCheckedChange={setWeekendMode}
                className="data-[state=checked]:bg-[#735AE2]"
              />
            </label>

            {isAdmin && (
              <>
                <Button
                  data-testid="share-btn"
                  onClick={handleShare}
                  variant="outline"
                  className="rounded-none bg-transparent border border-white/[0.2] text-white hover:bg-white/[0.05] hover:border-white/40 px-5 py-2.5 font-mono text-xs uppercase tracking-widest font-medium h-auto"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>

                <Button
                  data-testid="add-clock-btn"
                  onClick={openAddSheet}
                  disabled={totalClocks >= MAX_CLOCKS}
                  className="rounded-none bg-[#735AE2] text-white hover:bg-[#8A75EA] px-5 py-2.5 font-mono text-xs uppercase tracking-widest font-medium h-auto disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add clock · {totalClocks}/{MAX_CLOCKS}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Pinned Austin (HQ) - Terminal block */}
        <div className="mb-16">
          <div className="font-mono text-xs uppercase tracking-[0.4em] text-zinc-500 mb-4">
            // headquarters
          </div>
          <FeaturedHQCard
            clock={AUSTIN_CLOCK}
            format24hr={format24hr}
            allClocks={allClocksForFinder}
            holidays={holidays}
          />
        </div>

        {/* Team Clocks - Reorderable */}
        {clocks.length > 0 && (
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.4em] text-zinc-500 mb-4">
              // team_locations
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={clocks.map((c) => c.id)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0 border-l border-t border-white/[0.08]">
                  {clocks.map((clock) => (
                    <ClockCard
                      key={clock.id}
                      clock={clock}
                      format24hr={format24hr}
                      onEdit={openEditSheet}
                      onDelete={handleDeleteClock}
                      isPinned={false}
                      isSortable={true}
                      weekendMode={weekendMode}
                      holidays={holidays}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {clocks.length === 0 && (
          <div className="flex flex-col items-center justify-center min-h-[200px] text-center mt-8 border border-dashed border-white/[0.12] p-8">
            <Clock className="w-12 h-12 text-[#735AE2] mb-3 opacity-50" />
            <h2 className="font-heading text-xl font-bold text-white mb-2">No team clocks yet</h2>
            <p className="font-body text-zinc-400 mb-6 text-sm">Add timezones for your global team members</p>
            <Button
              onClick={() => setIsSheetOpen(true)}
              className="rounded-none bg-[#735AE2] text-white hover:bg-[#8A75EA] px-5 py-2.5 font-mono text-xs uppercase tracking-widest font-medium h-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add team clock
            </Button>
          </div>
        )}

        {/* Meeting Finder */}
        {clocks.length > 0 && (
          <MeetingFinder clocks={allClocksForFinder} holidays={holidays} />
        )}
      </div>

      <AddClockSheet
        open={isSheetOpen}
        onOpenChange={closeSheet}
        onSubmit={editingClock ? handleEditClock : handleAddClock}
        editingClock={editingClock}
      />

      {/* Shared config merge/replace dialog */}
      <AlertDialog open={!!pendingSharedConfig} onOpenChange={(o) => !o && cancelShared()}>
        <AlertDialogContent
          className="bg-[#0A0A0A] border border-white/[0.12] text-white rounded-none"
          data-testid="share-import-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-bold text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#735AE2]" />
              Shared dashboard detected
            </AlertDialogTitle>
            <AlertDialogDescription className="font-body text-zinc-400">
              Someone shared a dashboard with{' '}
              <span className="text-[#9363E6] font-semibold">
                {pendingSharedConfig?.clocks?.length || 0} clock
                {pendingSharedConfig?.clocks?.length === 1 ? '' : 's'}
              </span>
              . How would you like to apply it?
              <span className="block mt-3 font-mono text-xs text-zinc-500">
                <span className="text-[#735AE2]">{'>'}</span> Merge: add new clocks to your current dashboard
                <br />
                <span className="text-[#735AE2]">{'>'}</span> Replace: overwrite your dashboard with the shared one
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              onClick={cancelShared}
              className="rounded-none bg-transparent border border-white/20 text-white hover:bg-white/[0.05] font-mono text-xs uppercase tracking-widest"
              data-testid="share-import-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={applySharedMerge}
              className="rounded-none bg-transparent border border-[#735AE2]/60 text-[#9D8AEC] hover:bg-[#735AE2]/10 hover:text-white font-mono text-xs uppercase tracking-widest"
              data-testid="share-import-merge"
            >
              Merge
            </AlertDialogAction>
            <AlertDialogAction
              onClick={applySharedReplace}
              className="rounded-none bg-[#735AE2] text-white hover:bg-[#8A75EA] font-mono text-xs uppercase tracking-widest"
              data-testid="share-import-replace"
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TimezoneDashboard;
